import { useState, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '蜜桃电影',
  url: 'https://mitaomovie.com/',
  description: '蜜桃电影 - 精选好片在线观看推荐平台，为您提供专业的电影推荐和深度影评',
  foundingDate: '2023',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'contact@mitaomovie.com',
    availableLanguage: ['Chinese'],
  },
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于蜜桃电影',
  description: '了解蜜桃电影的品牌故事、用户口碑和联系方式',
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mitaomovie.com/' },
    { '@type': 'ListItem', position: 2, name: '关于我们' },
  ],
};

const testimonials = [
  {
    name: '张先生',
    role: '资深影迷',
    content: '蜜桃电影的推荐真的太准了！每次不知道看什么的时候就来逛逛，总能找到让我惊喜的好片。编辑的影评也很专业，让我对电影有了更深的理解。',
    rating: 5,
  },
  {
    name: '李女士',
    role: '电影爱好者',
    content: '分类功能特别好用，我特别喜欢悬疑片，蜜桃电影的悬疑推荐基本没有踩过雷。更新速度也很快，新片一上就能看到推荐。',
    rating: 5,
  },
  {
    name: '王同学',
    role: '大学生',
    content: '作为一个电影专业的学生，蜜桃电影是我每天必看的网站。影评文章的深度和专业性超出了我的预期，很多分析角度都让我受益匪浅。',
    rating: 5,
  },
  {
    name: '赵女士',
    role: '全职妈妈',
    content: '带娃之余唯一的娱乐就是看电影，蜜桃电影的精选推荐帮我节省了很多选片时间。网站的界面也很清爽，用起来很舒服，手机浏览体验也特别好。',
    rating: 4,
  },
  {
    name: '陈先生',
    role: '程序员',
    content: '周末放松就看蜜桃电影推荐的动作片，每部都精彩。最近看了他们推荐的《暗夜追踪》，真的燃爆了！推荐给同事后同事也成了忠实用户。',
    rating: 5,
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9slmdh4ne17ei0sdjd0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const schema = { ...aboutSchema, ...webPageSchema, ...breadcrumbSchema };

  return (
    <>
      <SEO
        title="关于我们 - 蜜桃电影 | 品牌介绍与联系方式"
        description="了解蜜桃电影的品牌故事、发展历程和核心团队。蜜桃电影致力于为华语观众提供最专业的电影推荐和深度影评服务，联系我们获取更多信息。"
        keywords="蜜桃电影,关于蜜桃电影,电影推荐平台,影评网站,联系我们"
        canonicalUrl="https://mitaomovie.com/about"
        ogType="website"
        schema={schema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          {/* Brand Intro */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-6">
                关于蜜桃电影
              </h1>
              <p className="text-foreground-600 text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
                蜜桃电影创立于2023年，是一个专注于为华语观众提供优质电影推荐和深度影评的内容平台。
                我们相信每一部好电影都值得被发现，每一位观众都值得拥有最好的观影体验。
                蜜桃电影的编辑团队由资深影评人和电影爱好者组成，致力于用专业、真诚的推荐，
                帮助您在浩瀚的影片海洋中找到属于自己的那部好片。
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
                <div className="lg:col-span-1">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                    我们的故事
                  </h2>
                  <p className="text-foreground-500 text-sm leading-relaxed">
                    从一个小小的影迷社群起步，到如今每月为数十万影迷提供精选推荐
                  </p>
                </div>
                <div className="lg:col-span-2 space-y-4">
                  <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                    蜜桃电影的诞生源于一个简单的想法：在这个信息爆炸的时代，如何帮助观众在海量影片中快速找到值得看的好电影？
                    我们的创始人是一位有着二十年观影经验的资深影迷，他发现身边的朋友常常因为不知道看什么而放弃观影。
                    于是，蜜桃电影应运而生。
                  </p>
                  <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                    从最初的手工整理推荐片单，到如今涵盖八大类型、数百部精选影片的专业推荐平台，
                    蜜桃电影始终坚持"精选、专业、真诚"的核心理念。我们的每一篇推荐和影评，都经过至少两位编辑的审阅和讨论，
                    确保推荐质量始终如一。
                  </p>
                  <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                    2024年，蜜桃电影进行了全面升级，新增了分类浏览、深度影评、用户互动等功能，
                    让观影推荐变得更加便捷和个性化。我们相信，蜜桃电影不仅是一个推荐平台，
                    更是一个连接电影与观众的温暖社区。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                <div className="text-center">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-accent-500 mb-1">500+</div>
                  <p className="text-sm text-foreground-500">精选影片</p>
                </div>
                <div className="text-center">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-accent-500 mb-1">200+</div>
                  <p className="text-sm text-foreground-500">深度影评</p>
                </div>
                <div className="text-center">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-accent-500 mb-1">8</div>
                  <p className="text-sm text-foreground-500">电影类型</p>
                </div>
                <div className="text-center">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-accent-500 mb-1">30万+</div>
                  <p className="text-sm text-foreground-500">月活用户</p>
                </div>
              </div>
            </div>
          </section>

          {/* Reputation / Testimonials */}
          <section id="reputation" className="py-14 md:py-20 bg-background-50">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                  用户口碑
                </h2>
                <p className="text-foreground-500 text-sm md:text-base">
                  听听蜜桃电影用户们怎么说
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {testimonials.map((t, index) => (
                  <div
                    key={index}
                    className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i
                            className={`text-sm ${
                              i < t.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'
                            }`}
                          ></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                      "{t.content}"
                    </p>
                    <div className="text-xs text-foreground-500">
                      {t.name} · {t.role}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section id="contact" className="py-14 md:py-20 bg-background-100">
            <div className="max-w-2xl mx-auto px-4 md:px-6">
              <div className="text-center mb-8 md:mb-10">
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-foreground-500 text-sm md:text-base">
                  有任何问题或建议？欢迎给我们留言，蜜桃电影团队会在24小时内回复
                </p>
              </div>

              <form
                ref={formRef}
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/70"
              >
                {/* Honeypot */}
                <input
                  type="text"
                  name="phone_alt"
                  className="honeypot-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入留言主题"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                </div>

                {formStatus === 'error' && formError && (
                  <div className="mb-4 p-3 bg-accent-100/80 text-accent-700 rounded-lg text-sm">
                    {formError}
                  </div>
                )}

                {formStatus === 'success' && (
                  <div className="mb-4 p-3 bg-primary-100 text-primary-700 rounded-lg text-sm">
                    感谢您的留言！我们会在24小时内回复您。
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                </button>
              </form>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}